import { useEffect, useRef, useState } from "react";
import { trackEvent } from "@/utils/posthog";
import { flushPendingKitConversion, queueKitConversion } from "@/utils/kit-conversion";

const FORM_UID = "773f4186a5";
const CONTACT = "mailto:amelia@technical-leaders.com?subject=Speaking%20Inquiry";
const REVISION = "2026-10-23";

const SpeakingInquiry = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<"loading" | "ready" | "unavailable" | "accepted">("loading");

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let started = false;
    let accepted = false;
    let retryTimer: ReturnType<typeof setInterval> | undefined;
    let retries = 0;

    const handleStart = () => {
      if (started) return;
      started = true;
      trackEvent("Speaking Inquiry Started", { page: "/speak", location: "speaking_request_form", speaking_revision: REVISION });
    };
    const handleComplete = (event: Event) => {
      const uid = (event as CustomEvent<{ uid?: string }>).detail?.uid;
      if (accepted || (uid && uid !== FORM_UID)) return;
      accepted = true;
      setState("accepted");
      queueKitConversion("Speaking Request Submitted", {
        source: "kit_form", kit_form_uid: FORM_UID, location: "speaking_page",
        source_page_path: "/speak", speaking_revision: REVISION,
      });
      if (flushPendingKitConversion() === "waiting_for_posthog") {
        retryTimer = setInterval(() => {
          if (flushPendingKitConversion() !== "waiting_for_posthog" || ++retries >= 100) clearInterval(retryTimer);
        }, 100);
      }
    };
    const prepareForm = () => {
      const form = container.querySelector("form");
      if (!form || accepted) return;
      const fields = form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
        "input:not([type='hidden']):not([type='submit']):not([type='checkbox']):not([type='radio']), textarea, select",
      );
      fields.forEach((field, index) => {
        if (field.labels?.length) return;
        const question = field.getAttribute("aria-label")?.trim() || field.getAttribute("placeholder")?.trim();
        if (!question) return;
        field.id ||= `speaking-field-${index}`;
        const label = document.createElement("label");
        label.htmlFor = field.id;
        label.className = "speak-field-label";
        label.textContent = `${question} (${field.required ? "required" : "optional"})`;
        field.before(label);
      });
      // Keep Kit's button, validation, spinner, and request handling intact.
      const submitText = form.querySelector('button[data-element="submit"] > span');
      if (submitText && submitText.textContent === "Submit") submitText.textContent = "Send speaking inquiry";
      setState("ready");
    };

    container.addEventListener("focusin", handleStart);
    container.addEventListener("ckjs:submission:complete", handleComplete);
    const observer = new MutationObserver(prepareForm);
    observer.observe(container, { childList: true, subtree: true });
    const script = document.createElement("script");
    script.async = true;
    script.setAttribute("data-uid", FORM_UID);
    script.src = `https://techleaders.kit.com/${FORM_UID}/index.js`;
    container.appendChild(script);
    const fallbackTimer = setTimeout(() => {
      if (!accepted && !container.querySelector("form")) setState("unavailable");
    }, 6000);
    return () => {
      container.removeEventListener("focusin", handleStart);
      container.removeEventListener("ckjs:submission:complete", handleComplete);
      observer.disconnect();
      clearTimeout(fallbackTimer);
      clearInterval(retryTimer);
      container.replaceChildren();
    };
  }, []);

  return (
    <div>
      <div ref={containerRef} className="speak-inquiry ph-no-capture" />
      {state === "loading" && <p role="status" className="text-muted-foreground">Loading the inquiry form…</p>}
      {state === "unavailable" && <p role="status" className="text-muted-foreground">The inquiry form couldn't load. Please email Amelia below with your event details.</p>}
      {state === "accepted" && <p role="status" className="mt-4 font-semibold">Your inquiry has been submitted. Watch your inbox for next steps from our team.</p>}
      <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
        Prefer email? Have an event brief ready?{" "}
        <a className="speak-text-link break-words" href={CONTACT} onClick={() => trackEvent("Speaking Contact Email Clicked", { page: "/speak", location: "speaking_request_form", speaking_revision: REVISION })}>Contact Amelia directly</a>.
      </p>
    </div>
  );
};

export default SpeakingInquiry;
